import { readMe, createItem } from '@directus/sdk'
import directus from '@/lib/directus'
import { User } from '@/types'
import { AppConfig } from '@/config/AppConfig'

interface AuthResponse {
  success: boolean
  error?: Error
  user?: User
  token?: string
  redirectTo?: string
}

interface LoginCredentials {
  email: string
  password: string
}

interface RegisterData {
  email: string
  password: string
  firstName: string
  lastName: string
  phoneNumber?: string
}

const DIRECTUS_AUTH_KEY = 'directus_auth'
const USER_DATA_KEY = 'userData'
const ACCESS_TOKEN_KEY = 'access_token'
const REFRESH_TOKEN_KEY = 'refresh_token'

const storeAuthResult = (result: any) => {
  localStorage.setItem(DIRECTUS_AUTH_KEY, JSON.stringify(result))
  if (result.access_token) localStorage.setItem(ACCESS_TOKEN_KEY, result.access_token)
  if (result.refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, result.refresh_token)
}

const storeUserData = (userData: User) => {
  localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData))
}

const clearAuthData = () => {
  localStorage.removeItem(DIRECTUS_AUTH_KEY)
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(USER_DATA_KEY)
}

const buildUserFromMe = (
  me: any,
  overrides: Partial<User> = {}
): User => ({
  id: me.id,
  email: me.email,
  displayName: `${me.first_name || ''} ${me.last_name || ''}`.trim() || me.email,
  commercialId: (me.commercialId as string | undefined) ?? overrides.commercialId,
  referralCode: (me.referralCode as string | undefined) ?? overrides.referralCode,
  // Directus uses snake_case for phone; keep camelCase alias too.
  phone_number: (me.phone_number as string | undefined) ?? overrides.phoneNumber ?? overrides.phone_number,
  phoneNumber: (me.phone_number as string | undefined) ?? overrides.phoneNumber ?? overrides.phone_number,
  totalEarnings: me.totalEarnings || 0,
  referralCount: me.referralCount || 0,
  balance: me.balance || 0,
  accountType: me.accountType || 'MOBCASH',
  createdAt: me.date_created ? new Date(me.date_created) : new Date(),
  lastActivity: me.date_updated ? new Date(me.date_updated) : new Date(),
  photoURL: ((me.avatar as string | null) || null) || undefined,
  ...overrides,
})

export const authService = {
  /**
   * Login with email and password using Directus SDK
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const result = await directus.login(credentials.email, credentials.password)
      storeAuthResult(result)

      const me = await directus.request(readMe())

      const userData = buildUserFromMe(me)
      storeUserData(userData)

      return {
        success: true,
        user: userData,
        token: result.access_token || undefined,
        redirectTo: '/dashboard'
      }
    } catch (error: any) {
      console.error('Login error:', error)
      return {
        success: false,
        error: new Error(error.message || 'Email ou mot de passe incorrect')
      }
    }
  },

  /**
   * Register new user using Directus
   */
  async register(data: RegisterData): Promise<AuthResponse> {
    try {
      const commercialCode = data.phoneNumber || this.generateReferralCode()

      console.log('📝 Tentative d\'inscription pour:', data.email)

      const API_URL = AppConfig.baseUrl

      // 1. Create user via public /users/register endpoint
      // NOTE: keep payload minimal — backend whitelists only core user fields.
      // commercialId/referralCode are generated client-side and persisted
      // locally via userData overrides after auto-login.
      const registerResponse = await fetch(`${API_URL}/users/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          first_name: data.firstName,
          last_name: data.lastName,
        })
      })

      if (!registerResponse.ok) {
        const errorData = await registerResponse.json().catch(() => ({}))
        const message = errorData.errors?.[0]?.message || errorData.message || errorData.error?.message || 'Erreur lors de la création du compte'

        if (registerResponse.status === 409 || message.includes('already exists') || message.includes('existe déjà')) {
          return {
            success: false,
            error: new Error('Un compte avec cet email existe déjà')
          }
        }

        return {
          success: false,
          error: new Error(message)
        }
      }

      console.log('✅ Utilisateur créé, statut:', registerResponse.status)

      // 2. Auto-login after registration
      const loginResult = await directus.login(data.email, data.password)
      storeAuthResult(loginResult)

      // 3. Get current user info
      const me = await directus.request(readMe())
      console.log('📤 User data after register:', me)

      // 4. Create commercial relation (mandatory for affiliate link generation)
      try {
        await directus.request(createItem('commercial_relations', {
          user_id: me.id,
          commercial_phoneNumber: data.phoneNumber,
          nom: data.lastName,
          prenom: data.firstName,
          application: AppConfig.commercialApplicationId,
        }))
        console.log('✅ Relation commerciale créée')
      } catch (commercialError: any) {
        const commercialMessage = commercialError?.errors?.[0]?.message
          || commercialError?.message
          || JSON.stringify(commercialError)
        console.error('❌ Erreur lors de la création de la relation commerciale:', commercialMessage, commercialError)
        // return {
        //   success: false,
        //   error: new Error(`Erreur lors de la création du profil commercial: ${commercialMessage}`)
        // }
      }

      // 5. Return full user data
      const userData = buildUserFromMe(me, {
        phoneNumber: data.phoneNumber,
        phone_number: data.phoneNumber,
        commercialId: commercialCode,
        referralCode: commercialCode,
        totalEarnings: 0,
        referralCount: 0,
        balance: 0
      })

      storeUserData(userData)

      return {
        success: true,
        user: userData,
        token: loginResult.access_token || undefined,
        redirectTo: '/dashboard'
      }
    } catch (error: any) {
      console.error('Register error:', error)
      return {
        success: false,
        error: new Error(error.message || 'Erreur lors de l\'inscription')
      }
    }
  },

  /**
   * Logout user using Directus SDK
   */
  async logout(): Promise<void> {
    try {
      await directus.logout()
    } catch (error) {
      console.error('Logout error:', error)
    }
    clearAuthData()
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem(ACCESS_TOKEN_KEY)
  },

  /**
   * Get current authenticated user
   */
  getCurrentUser(): User | null {
    try {
      const userData = localStorage.getItem(USER_DATA_KEY)
      return userData ? JSON.parse(userData) : null
    } catch (error) {
      console.error('Error parsing user data:', error)
      return null
    }
  },

  /**
   * Get auth token
   */
  getAuthToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY)
  },

  /**
   * Set auth token
   */
  setAuthToken(token: string): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, token)
  },

  /**
   * Generate unique commercial ID
   */
  generateCommercialId(): string {
    const timestamp = Date.now().toString(36)
    const random = Math.random().toString(36).substring(2, 8)
    return `MB${timestamp.toUpperCase()}${random.toUpperCase()}`
  },

  /**
   * Generate unique referral code
   */
  generateReferralCode(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    let result = ''
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return result
  },

  /**
   * Update current user data
   */
  updateCurrentUser(userData: Partial<User>): void {
    const currentUser = this.getCurrentUser()
    if (currentUser) {
      const updatedUser = { ...currentUser, ...userData }
      storeUserData(updatedUser)
    }
  },

  /**
   * Refresh user data from Directus API
   */
  async refreshUserData(): Promise<AuthResponse> {
    try {
      const me = await directus.request(readMe())

      // Keep the phone entered at registration: /users/me does not return it,
      // and the dashboard needs it to create a missing commercial_relations row.
      const current = this.getCurrentUser()
      const storedPhone = current?.id === me.id ? current?.phone_number || current?.phoneNumber : undefined
      const userData = buildUserFromMe(me, storedPhone ? { phone_number: storedPhone, phoneNumber: storedPhone } : {})
      storeUserData(userData)

      return {
        success: true,
        user: userData
      }
    } catch (error: any) {
      return {
        success: false,
        error: new Error(error.message || 'Erreur lors du rafraîchissement')
      }
    }
  }
}

export default authService
